interface FocusTrapOptions {
  element: HTMLElement;
  initialFocus?: HTMLElement | string | undefined;
  returnFocus?: HTMLElement;
  escapeDeactivates?: boolean;
  blockScroll?: boolean;
  blockScrollOn?: HTMLElement | string;
  onActivate?: () => void;
  onDeactivate?: () => void;
}

class FocusTrap {
  private element: HTMLElement;
  private previouslyFocusedElement: HTMLElement | null = null;
  private isActive = false;
  private initialFocus?: HTMLElement | string;
  private returnFocus?: HTMLElement;
  private escapeDeactivates: boolean;
  private blockScroll: boolean;
  private blockScrollOn: HTMLElement | string;
  private onActivate?: () => void;
  private onDeactivate?: () => void;

  // Focusable selectors
  private focusableSelectors = [
    'input:not([disabled]):not([type="hidden"]):not([aria-hidden="true"])',
    'select:not([disabled]):not([aria-hidden="true"])',
    'textarea:not([disabled]):not([aria-hidden="true"])',
    'button:not([disabled]):not([aria-hidden="true"])',
    'a[href]:not([aria-hidden="true"])',
    'area[href]:not([aria-hidden="true"])',
    'iframe:not([aria-hidden="true"])',
    'object:not([aria-hidden="true"])',
    'embed:not([aria-hidden="true"])',
    '[contenteditable]:not([aria-hidden="true"])',
    '[tabindex]:not([tabindex^="-"]):not([aria-hidden="true"])',
  ].join(', ');

  constructor(options: FocusTrapOptions) {
    this.element = options.element;
    this.initialFocus = options.initialFocus;
    this.returnFocus = options.returnFocus;
    this.escapeDeactivates = options.escapeDeactivates ?? true;
    this.blockScroll = options.blockScroll ?? false;
    this.blockScrollOn = options.blockScrollOn ?? document.body;
    this.onActivate = options.onActivate;
    this.onDeactivate = options.onDeactivate;

    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleFocusIn = this.handleFocusIn.bind(this);
  }

  private getFocusableElements(): HTMLElement[] {
    const elements = Array.from(this.element.querySelectorAll(this.focusableSelectors)) as HTMLElement[];

    return elements.filter((el) => {
      // Check if element is visible and not disabled
      const style = window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden' && !el.hasAttribute('disabled') && el.tabIndex >= 0;
    });
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (!this.isActive) return;

    // Handle Escape key
    if (e.key === 'Escape' && this.escapeDeactivates) {
      e.preventDefault();
      this.deactivate();
      return;
    }

    // Handle Tab key
    if (e.key === 'Tab') {
      const focusableElements = this.getFocusableElements();

      if (focusableElements.length === 0) {
        e.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);

      if (e.shiftKey) {
        // Shift + Tab (backward)
        if (document.activeElement === firstElement || currentIndex === -1) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab (forward)
        if (document.activeElement === lastElement || currentIndex === -1) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }
  }

  private handleFocusIn(e: FocusEvent): void {
    if (!this.isActive) return;

    const target = e.target as HTMLElement;

    // If focus is outside the trap element, redirect it back
    if (!this.element.contains(target)) {
      e.preventDefault();
      const focusableElements = this.getFocusableElements();
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    }
  }

  public activate(): void {
    if (this.isActive) return;

    // Store the currently focused element
    this.previouslyFocusedElement = document.activeElement as HTMLElement;

    // Add event listeners
    document.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('focusin', this.handleFocusIn);

    this.isActive = true;

    // Set initial focus
    this.setInitialFocus();

    // Check if scrolling should be blocked.
    if (this.blockScroll) {
      this._blockScroll();
    }

    // Call onActivate callback
    if (this.onActivate) {
      this.onActivate();
    }
  }

  public deactivate(): void {
    if (!this.isActive) return;

    // Remove event listeners
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('focusin', this.handleFocusIn);

    this.isActive = false;

    // Return focus to previously focused element
    this.returnFocusToPrevious();

    // Check if scrolling should have been blocked.
    if (this.blockScroll) {
      this._unblockScroll();
    }

    // Call onDeactivate callback
    if (this.onDeactivate) {
      this.onDeactivate();
    }
  }

  private setInitialFocus(): void {
    let elementToFocus: HTMLElement | null = null;

    if (this.initialFocus) {
      if (typeof this.initialFocus === 'string') {
        elementToFocus = this.element.querySelector(this.initialFocus);
      } else {
        elementToFocus = this.initialFocus;
      }
    }

    // If no initial focus specified or element not found, focus first focusable element
    if (!elementToFocus) {
      const focusableElements = this.getFocusableElements();
      elementToFocus = focusableElements[0] || null;
    }

    if (elementToFocus) {
      elementToFocus.focus();
    }
  }

  private returnFocusToPrevious(): void {
    let elementToFocus: HTMLElement | null = null;

    if (this.returnFocus) {
      elementToFocus = this.returnFocus;
    } else if (this.previouslyFocusedElement) {
      elementToFocus = this.previouslyFocusedElement;
    }

    if (elementToFocus && document.contains(elementToFocus)) {
      elementToFocus.focus();
    }
  }

  public updateElement(element: HTMLElement): void {
    this.element = element;
  }

  public isActivated(): boolean {
    return this.isActive;
  }

  private _blockScroll(): void {
    const blockedOn = typeof this.blockScrollOn === 'string' ? document.querySelector<HTMLElement>(this.blockScrollOn) : this.blockScrollOn;

    if (blockedOn) {
      blockedOn.style.overflow = 'hidden';
    }
  }

  private _unblockScroll(): void {
    const blockedOn = typeof this.blockScrollOn === 'string' ? document.querySelector<HTMLElement>(this.blockScrollOn) : this.blockScrollOn;

    if (blockedOn) {
      blockedOn.style.overflow = '';
    }
  }
}

// Usage example and utility functions
const FocusTrapUtils = {
  // Create a focus trap for a modal
  createModalTrap(modalElement: HTMLElement, options: Partial<FocusTrapOptions> = {}): FocusTrap {
    return new FocusTrap({
      element: modalElement,
      initialFocus: options.initialFocus || modalElement.querySelector<HTMLElement>('button, input, select, textarea') || undefined,
      escapeDeactivates: true,
      blockScroll: true,
      blockScrollOn: document.body,
      ...options,
    });
  },

  // Create a focus trap for a dropdown menu
  createDropdownTrap(dropdownElement: HTMLElement, triggerElement: HTMLElement): FocusTrap {
    return new FocusTrap({
      element: dropdownElement,
      returnFocus: triggerElement,
      escapeDeactivates: true,
    });
  },

  // Create a focus trap for a sidebar/drawer
  createSidebarTrap(sidebarElement: HTMLElement, options: Partial<FocusTrapOptions> = {}): FocusTrap {
    return new FocusTrap({
      element: sidebarElement,
      escapeDeactivates: true,
      blockScroll: true,
      blockScrollOn: document.body,
      ...options,
    });
  },
};

// Example usage in Shopify Liquid context
declare global {
  interface Window {
    FocusTrap: typeof FocusTrap;
    FocusTrapUtils: typeof FocusTrapUtils;
  }
}

// Make available globally for Liquid templates
window.FocusTrap = FocusTrap;
window.FocusTrapUtils = FocusTrapUtils;

// Export for module usage
export { FocusTrap, FocusTrapUtils };
export type { FocusTrapOptions };
